import React, { useMemo } from 'react';
import theme from './StyleCard.module.scss';
import { Button, Card, Carousel, Badge } from '@ygtechnologies/rubus-ui';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { classnames, MarkdownDisplay, Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';
import { Link, useParams } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { OptionPresetGroupDto } from '@/services/option-preset/option-preset-group';
// Raw helper for SellerStyleCard, which renders outside the configurator (no showroom context).
// StyleCard itself uses the showroom-aware helper from useImageUrls.
import { getOptionPresetImageUrl } from '@/services/option-preset/option-preset-image';
import { useImageUrls } from '../useImageUrls';
import { ConfiguratorPresetDto, ConfiguratorPresetGroupDto } from '@/services/configurator';
import { faCalendarLinesPen } from '@fortawesome/pro-light-svg-icons';
import { StyleButton } from '../SelectStyleBtn';
import { BuyerOptionPresetStatus } from '@/types';
import { useOptionPresets } from '@/services/option-preset';
import { buildConfiguratorUrl } from '../utils/buildConfiguratorUrl';

export interface StyleCardProps {
    style: ConfiguratorPresetDto;
    styleGroup: ConfiguratorPresetGroupDto;
}

export const StyleCard: React.FC<StyleCardProps> = ({ style, styleGroup }) => {
    const { roomId } = useParams<{ roomId: string }>();
    const { baseUrl, selectedDeadlineId } = useOptionConfigurator();
    const { getOptionPresetImageUrl } = useImageUrls();

    // Keep all hooks above any early return — `selectedDeadlineId` is null on first render in the
    // showroom (set by an effect once data loads), so returning before a hook changes the hook
    // count between renders and crashes with "Rendered more hooks than during the previous render".
    // Copy before sorting so we don't mutate the cached configurator data in place.
    const sortedImages = useMemo(() => {
        return [...style.images].sort((a, b) => a.sortOrder - b.sortOrder);
    }, [style.images]);

    if (!selectedDeadlineId) {
        return null;
    }

    return (
        <Card className={theme.styleMainCard} key={style.id}>
            <div className={theme.styleContent}>
                <h2 className={theme.styleTitle}>
                    <span>{style.name}</span>
                    <span>
                        <BuyerCurrencyDisplay value={style.price} />
                    </span>
                </h2>

                <MarkdownDisplay className={theme.description} markdown={style.description} />

                <div className={theme.buttons}>
                    <Button
                        as={Link}
                        to={buildConfiguratorUrl({
                            baseUrl,
                            deadlineId: selectedDeadlineId,
                            segment: roomId
                                ? `room/${roomId}/style-group/${styleGroup.id}/style/${style.id}`
                                : `style-group/${styleGroup.id}/style/${style.id}`,
                        })}
                        size="lg"
                        variant="primary">
                        <Trans>More details</Trans>
                    </Button>
                    <StyleButton style={style} roomId={roomId} isLocked={styleGroup.isLocked} />
                </div>
            </div>
            {/* {sortedImages[0] && (
                <Thumbnail className={theme.thumbnail} src={getOptionPresetImageUrl(sortedImages[0].id)} alt={style.name} />
            )} */}

            <Carousel
                mode="indicators"
                enableLightbox={false}
                className={theme.carousel}
                height={500}
                src={sortedImages.map((image) => ({
                    type: 'image',
                    src: getOptionPresetImageUrl(image.id),
                    thumbnailSrc: getOptionPresetImageUrl(image.id),
                    title: style.name,
                }))}
            />
        </Card>
    );
};

export interface SellerStyleCardProps {
    group: OptionPresetGroupDto;
}

export const SellerStyleCard: React.FC<SellerStyleCardProps> = ({ group }) => {
    const { data: presets } = useOptionPresets(group.id);
    console.log('presets', presets);
    return (
        <Card className={classnames(theme.styleMainCard, theme.sellerStyleCard)} key={group.id}>
            <div className={theme.styleContent}>
                <Badge
                    appearance="subtle"
                    variant={group.status === BuyerOptionPresetStatus.Published ? 'success' : 'warning'}
                    className={theme.statusPill}>
                    {group.status === BuyerOptionPresetStatus.Published ? <Trans>Published</Trans> : <Trans>Pending</Trans>}
                </Badge>
                <h2 className={theme.styleTitle}>
                    <span>{group.name}</span>
                </h2>

                <MarkdownDisplay className={theme.description} markdown={group.description} />

                <Button
                    as={Link}
                    to={`/projects/${group.projectId}/options/styles/group/${group.id}`}
                    size="lg"
                    variant="tertiary"
                    className={theme.changeStyleGroupButton}
                    icon={faCalendarLinesPen}>
                    <Trans>Edit style group</Trans>
                </Button>
            </div>
            <div className={theme.imageWrapper}>
                {presets?.slice(0, 4).map((preset) => (
                    <div key={preset.id} className={theme.presetItem}>
                        <Thumbnail
                            draggable={false}
                            src={preset.images.length > 0 ? getOptionPresetImageUrl(preset.images[0]?.id) : ''}
                        />
                        <Badge appearance="subtle" className={theme.presetName}>
                            {preset.name}
                        </Badge>
                    </div>
                ))}
            </div>
        </Card>
    );
};
